/**
 * Date-bucketing helpers for list pages (Absences, Relief, Exam Invigilation).
 *
 * - `getSGToday()` returns YYYY-MM-DD in Asia/Singapore so client-side bucketing
 *   matches the user's local school day regardless of browser timezone.
 * - `bucketByPhase()` groups items into Upcoming / Last 3 Days / Expired.
 * - `groupByMonth()` further groups each bucket by calendar month for display.
 * - `MONTH_PALETTE` is a 6-color rotating palette so adjacent months are
 *   visually distinct without becoming a rainbow.
 *
 * Within each month, items are returned in **descending date order**
 * (most recent first) per user preference.
 */

/** Today in Asia/Singapore as `YYYY-MM-DD`. */
export function getSGToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Singapore' }).format(new Date());
}

/** Add `n` days to a `YYYY-MM-DD` string and return the same format. */
export function addDays(yyyymmdd, n) {
  const d = new Date(yyyymmdd + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * Classify a single date string into one of the three phases.
 *  - 'upcoming'  : date >= today
 *  - 'last3'     : today-3 <= date < today
 *  - 'expired'   : date < today-3
 */
export function classifyDate(dateStr, today = getSGToday()) {
  if (!dateStr) return 'expired';
  const minusThree = addDays(today, -3);
  if (dateStr >= today) return 'upcoming';
  if (dateStr >= minusThree) return 'last3';
  return 'expired';
}

/**
 * Bucket items into the three phases. `getDate(item)` should return YYYY-MM-DD.
 *
 * Sort orders applied:
 *   • upcoming → ASC (chronological forward — closest first)
 *   • last3    → DESC (most recent first)
 *   • expired  → DESC (most recent first)
 */
export function bucketByPhase(items, getDate, today = getSGToday()) {
  const buckets = { upcoming: [], last3: [], expired: [] };
  for (const it of items) {
    const d = getDate(it);
    if (!d) continue;
    buckets[classifyDate(d, today)].push(it);
  }
  buckets.upcoming.sort((a, b) => (getDate(a) || '').localeCompare(getDate(b) || ''));
  buckets.last3.sort((a, b) => (getDate(b) || '').localeCompare(getDate(a) || ''));
  buckets.expired.sort((a, b) => (getDate(b) || '').localeCompare(getDate(a) || ''));
  return buckets;
}

/**
 * Group a flat array of items into ordered { monthKey, monthLabel, items } chunks.
 * `getDate(item)` should return YYYY-MM-DD. `order='asc'|'desc'` controls month order.
 * Items within each month preserve incoming order (callers should pass already-sorted items).
 */
export function groupByMonth(items, getDate, order = 'desc') {
  const map = new Map();
  for (const it of items) {
    const d = getDate(it);
    if (!d) continue;
    const key = d.slice(0, 7);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(it);
  }
  const sortFn = order === 'asc'
    ? ([a], [b]) => a.localeCompare(b)
    : ([a], [b]) => b.localeCompare(a);
  return Array.from(map.entries())
    .sort(sortFn)
    .map(([monthKey, list]) => ({
      monthKey,
      monthLabel: new Date(monthKey + '-01T00:00:00').toLocaleDateString('en-SG', {
        month: 'long',
        year: 'numeric',
      }),
      items: list,
    }));
}

/**
 * 6-color rotating palette for month headings. Each entry is a triple of
 * Tailwind classes (bg / text / border) so headings remain accessible at
 * sufficient contrast and consistent with the rest of the app.
 */
export const MONTH_PALETTE = [
  { bg: 'bg-blue-50',    text: 'text-blue-800',    border: 'border-blue-300',    dot: 'bg-blue-400' },
  { bg: 'bg-emerald-50', text: 'text-emerald-800', border: 'border-emerald-300', dot: 'bg-emerald-400' },
  { bg: 'bg-amber-50',   text: 'text-amber-800',   border: 'border-amber-300',   dot: 'bg-amber-400' },
  { bg: 'bg-purple-50',  text: 'text-purple-800',  border: 'border-purple-300',  dot: 'bg-purple-400' },
  { bg: 'bg-pink-50',    text: 'text-pink-800',    border: 'border-pink-300',    dot: 'bg-pink-400' },
  { bg: 'bg-indigo-50',  text: 'text-indigo-800',  border: 'border-indigo-300',  dot: 'bg-indigo-400' },
];

/** Stable color for a given monthKey (`YYYY-MM`) — uses month index mod palette length. */
export function colorForMonth(monthKey) {
  if (!monthKey) return MONTH_PALETTE[0];
  const m = parseInt(monthKey.slice(5, 7), 10);
  return MONTH_PALETTE[(m - 1) % MONTH_PALETTE.length];
}

/**
 * Phase metadata for the section header.
 * `key` matches `bucketByPhase` output keys.
 */
export const PHASES = [
  { key: 'upcoming', label: 'Upcoming',     dot: 'bg-emerald-500', text: 'text-emerald-700', testid: 'phase-upcoming' },
  { key: 'last3',    label: 'Last 3 Days',  dot: 'bg-amber-500',   text: 'text-amber-700',   testid: 'phase-last3' },
  { key: 'expired',  label: 'Expired',      dot: 'bg-slate-400',   text: 'text-slate-600',   testid: 'phase-expired' },
];
